import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { LuTrash2 } from 'react-icons/lu';
import { deleteVendorInvoice } from '../../../utils/agentsApi';
import {
  confirmDeleteJob,
  formatIngestionDate,
  JOB_COLUMN_WIDTHS,
} from './utils';

const DeleteJobButton = ({ jobId, vendorName, onDeleted }) => {
  const deleteMutation = useMutation({
    mutationFn: () => deleteVendorInvoice(jobId),
    onSuccess: () => {
      toast.success('Invoice deleted.');
      onDeleted();
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to delete invoice.');
    },
  });

  const handleClick = (e) => {
    e.stopPropagation();
    if (deleteMutation.isPending) {
      return;
    }
    if (!confirmDeleteJob(vendorName)) {
      return;
    }
    deleteMutation.mutate();
  };

  return (
    <button
      type="button"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: 'none',
        background: 'transparent',
        color: 'var(--darkGrey)',
        padding: '0.25rem',
        borderRadius: '0.25rem',
        cursor: deleteMutation.isPending ? 'not-allowed' : 'pointer',
        opacity: deleteMutation.isPending ? 0.5 : 1,
      }}
      onClick={handleClick}
      disabled={deleteMutation.isPending}
      aria-label={`Delete ${vendorName || 'invoice'}`}
    >
      <LuTrash2 />
    </button>
  );
};

const defaultColumns = [
  {
    key: 'vendorName',
    header: 'Vendor',
    render: (job) => job.vendorName || 'Unknown',
  },
  {
    key: 'jobType',
    header: 'Type',
    render: () => (
      <span
        style={{
          display: 'inline-block',
          padding: '0.1rem 0.45rem',
          borderRadius: '0.25rem',
          fontSize: '0.75rem',
          fontWeight: 600,
          background: 'var(--lightestGrey)',
          color: 'var(--darkGrey)',
          textTransform: 'uppercase',
        }}
      >
        Invoice
      </span>
    ),
  },
  {
    key: 'createdAt',
    header: 'Ingested',
    render: (job) => formatIngestionDate(job.createdAt),
  },
  {
    key: 'updatedBy',
    header: 'Last touched',
    render: (job) => job.updatedBy || '-',
  },
];

const columnWidthStyle = (column) => {
  const width = column.width ?? JOB_COLUMN_WIDTHS[column.key];
  if (!width && !column.style) {
    return undefined;
  }
  const base =
    typeof column.style === 'function' ? undefined : column.style || {};
  if (!width) {
    return base;
  }
  return { width, minWidth: width, maxWidth: width, ...base };
};

const VendorInvoiceJobTable = ({
  jobs,
  columns,
  onRowClick,
  onDeleted,
  showDelete = false,
}) => {
  const tableColumns = columns ?? defaultColumns;

  return (
    <div className="table-wrapper">
      <table style={{ tableLayout: 'fixed', width: '100%' }}>
        <thead>
          <tr>
            {tableColumns.map((column) => (
              <th key={column.key} style={columnWidthStyle(column)}>
                {column.header}
              </th>
            ))}
            {showDelete && (
              <th
                aria-label="Actions"
                style={{
                  width: JOB_COLUMN_WIDTHS.delete,
                  minWidth: JOB_COLUMN_WIDTHS.delete,
                  maxWidth: JOB_COLUMN_WIDTHS.delete,
                }}
              />
            )}
          </tr>
        </thead>
        <tbody>
          {jobs.map((job) => (
            <tr
              key={job.jobId}
              style={{ cursor: 'pointer' }}
              onClick={() => onRowClick(job.jobId)}
            >
              {tableColumns.map((column) => {
                const widthStyle = columnWidthStyle(column);
                const cellStyle =
                  typeof column.style === 'function'
                    ? { ...widthStyle, ...column.style(job) }
                    : widthStyle;
                return (
                  <td key={column.key} style={cellStyle}>
                    {column.render(job)}
                  </td>
                );
              })}
              {showDelete && (
                <td
                  style={{
                    width: JOB_COLUMN_WIDTHS.delete,
                    minWidth: JOB_COLUMN_WIDTHS.delete,
                    maxWidth: JOB_COLUMN_WIDTHS.delete,
                    textAlign: 'right',
                  }}
                >
                  <DeleteJobButton
                    jobId={job.jobId}
                    vendorName={job.vendorName}
                    onDeleted={onDeleted}
                  />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default VendorInvoiceJobTable;
